import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import apiService from '../../services/api';
import toast from 'react-hot-toast';

export default function ResetPasswordScreen() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const [showPass, setShowPass]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirm: '',
  });

  // Password strength
  const getStrength = (p) => {
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8)          s++;
    if (/[A-Z]/.test(p))        s++;
    if (/[0-9]/.test(p))        s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  };

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColor = ['', '#ef4444', '#f97316', '#eab308', '#22c55e'];
  const strength = getStrength(form.password);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await apiService.validateInvitationToken(token);
        if (res.success) {
          setInvitation(res.data);
        } else {
          setError('This invitation is invalid or has already been used.');
        }
      } catch {
        setError('This invitation is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (form.password !== form.confirm) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await apiService.acceptInvitation({
        token,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        password: form.password,
      });
      toast.success('Account created! Please log in.');
      navigate('/login');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) return (
    <div className="auth-page">
      <Loader2 className="animate-spin" size={32} style={{ color: '#FFC81E' }} />
    </div>
  );

  // ── Error state ────────────────────────────────────────────────────────────
  if (error) return (
    <div className="auth-page">
      <img src="/assets/clockout-lockup-hero-2400.png" alt="Clock-out" className="auth-logo" />
      <div className="auth-card" style={{ textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <AlertCircle size={32} style={{ color: '#ef4444' }} />
        </div>
        <h1 className="auth-title">Invalid invitation</h1>
        <p className="auth-subtitle" style={{ marginBottom: 24 }}>{error}</p>
        <button onClick={() => navigate('/login')} className="auth-btn-primary">
          Go to Login
        </button>
      </div>
    </div>
  );

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div className="auth-page">
      <img src="/assets/clockout-lockup-hero-2400.png" alt="Clock-out" className="auth-logo" />
      <div className="auth-card">

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h1 className="auth-title">
            You're invited!
          </h1>
          {invitation?.inviter && (
            <p className="auth-subtitle" style={{ marginBottom: 4 }}>
              <strong>{invitation.inviter}</strong> invited you
              {invitation.team && <> to join <strong>{invitation.team.name}</strong></>}
            </p>
          )}
          <p style={{ margin: 0, fontSize: 13, color: '#857a63' }}>{invitation?.email}</p>
        </div>

        {/* Role badge */}
        {invitation?.role && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <span style={{
              padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: '#FFC81E', color: '#1C1A14',
            }}>
              Role: {invitation.role.replace('_', ' ')}
            </span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Name row */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="auth-label">
                First Name *
              </label>
              <input
                value={form.firstName}
                onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                placeholder="Jane"
                required
                className="auth-input"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="auth-label">
                Last Name *
              </label>
              <input
                value={form.lastName}
                onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                placeholder="Smith"
                required
                className="auth-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="auth-label">
              Password *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Min. 8 characters"
                required
                className="auth-input"
                style={{ paddingRight: 40 }}
              />
              <button type="button" onClick={() => setShowPass(s => !s)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {showPass ? <EyeOff size={16} style={{ color: '#857a63' }} /> : <Eye size={16} style={{ color: '#857a63' }} />}
              </button>
            </div>

            {/* Strength bar */}
            {form.password && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} style={{
                      flex: 1, height: 3, borderRadius: 2,
                      background: i <= strength ? strengthColor[strength] : '#eadfc6',
                      transition: 'background 0.2s',
                    }} />
                  ))}
                </div>
                <p style={{ margin: 0, fontSize: 11, color: strengthColor[strength], fontWeight: 500 }}>
                  {strengthLabel[strength]}
                </p>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="auth-label">
              Confirm Password *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirm ? 'text' : 'password'}
                value={form.confirm}
                onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                placeholder="Repeat your password"
                required
                className="auth-input"
                style={{
                  paddingRight: 40,
                  ...(form.confirm && form.password !== form.confirm ? { borderColor: '#ef4444' } : null),
                }}
              />
              <button type="button" onClick={() => setShowConfirm(s => !s)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {showConfirm ? <EyeOff size={16} style={{ color: '#857a63' }} /> : <Eye size={16} style={{ color: '#857a63' }} />}
              </button>
            </div>
            {form.confirm && form.password !== form.confirm && (
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#ef4444' }}>Passwords do not match</p>
            )}
            {form.confirm && form.password === form.confirm && (
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#22c55e', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Check size={11} /> Passwords match
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || form.password !== form.confirm || form.password.length < 8}
            className="auth-btn-primary"
            style={{ marginTop: 4 }}
          >
            {submitting
              ? <><Loader2 size={16} className="animate-spin" /> Creating account...</>
              : <><Check size={16} /> Create Account</>
            }
          </button>

        </form>

        {/* Footer */}
        <p style={{ margin: '24px 0 0', textAlign: 'center', fontSize: 14, color: '#645b4a' }}>
          Already have an account?{' '}
          <button onClick={() => navigate('/login')} className="auth-link">
            Sign in
          </button>
        </p>

      </div>

      <p className="auth-footer">© {new Date().getFullYear()} Talk Nerdy To Me Consultancy Ltd. All rights reserved.</p>
    </div>
  );
}